import React, { useState, useRef, useMemo } from 'react';
import update from 'immutability-helper';

import { Row, Col, Button } from '../../bootstrap/index.jsx';
import Select, { ReadOnlyMultiSelectValues } from '../../common/Select.jsx';
import useDateTimePicker from '../../common/useDateTimePicker.js';
import ToggleSwitch from '../../common/ToggleSwitch.jsx';
import ActionButtons from './ActionButtons.jsx';
import ProjectSelectorInput from '../../common/ProjectSelectorInput.jsx';
import CommonColorPicker from '../../common/ColorPicker/CommonColorPicker.jsx';
import ImageUploadButton from '../../common/ImageUploader/ImageUploadButton.jsx';

import FormList from './FormList.jsx';
import PreFillContextList from './PreFillContextList.jsx';
import { ACTION_TYPES } from './phaseReducer.js';

import PhaseCollapser from './PhaseCollapser.jsx';
import { PhaseSectionElement } from './PhaseSectionElement.jsx';
import FormDataCaptureList from './FormDataCaptureList.jsx';

import { monitoring_report_update_path } from '~/routes';

const LIMIT_FIELD_VISIBILITY_BY_TYPE = {
  cross_fund_report: [
    "monitoring_report[initial_deadline]",
    "monitoring_report[deadline_reminders]",
    "monitoring_report[assignees]",
  ],
  survey: ["monitoring_report[initial_deadline]", "monitoring_report[deadline_reminders]"],
  call: ["monitoring_report[assignees]", "monitoring_report[initial_deadline]", "monitoring_report[deadline_reminders]", "monitoring_report[allowSelectPrefillSource]"],
  general: ["monitoring_report[assignees]"],
  disbursement: ["monitoring_report[assignees]"],
  batch_disbursement: ["monitoring_report[assignees]"],
  success_story: ["monitoring_report[initial_deadline]", "monitoring_report[deadline_reminders]","monitoring_report[assignees]"],
  governance: ["monitoring_report[project_ids][]"]
};

const ALLOW_FIELD_VISIBILITY_BY_FIELD_NAME = {
  "monitoring_report[disable_creating_new_drafts]": ["survey"],
  "monitoring_report[allow_only_assigned_submissions]": ["survey"],
  "monitoring_report[use_tenant_background]": ["survey"],
  "monitoring_report[use_custom_thank_you_page_texts]": ["survey"],
  "monitoring_report[use_custom_thank_you_page_image]": ["survey"],
  "monitoring_report[allow_single_submission]": ["survey"],
  "monitoring_report[allow_use_organization]": ["survey", "call", "general"],
  "monitoring_report[allow_create_organization]": ["survey", "call", "general"],
  "monitoring_report[allow_proceed_as_reviewed]": ["batch_disbursement"],
};

// No deadline reminders as there's no definition who should receive the message
const LIMIT_FIELD_VISIBILITY_BY_TARGET = {
  internal: [
    'monitoring_report[deadline_reminders]'
  ]
}

const LIMIT_FIELD_VISIBILITY_BY_CONFIGURATION_OPTION = [
  { option: "allow_multiple", value: 0, field: 'monitoring_report[project_ids][]' }
]

export const displayFormField = (field_name, report) => {
  if ((LIMIT_FIELD_VISIBILITY_BY_TYPE[report.report_type] || []).includes(field_name)) {
    return false;
  }

  if ((LIMIT_FIELD_VISIBILITY_BY_TARGET[report.visibility] || []).includes(field_name)) {
    return false;
  }

  if (
    LIMIT_FIELD_VISIBILITY_BY_CONFIGURATION_OPTION.filter(
      (f) => f.field == field_name && report[f.option] == f.value
    ).length > 0
  ) {
    return false;
  }

  if (field_name in ALLOW_FIELD_VISIBILITY_BY_FIELD_NAME && !(ALLOW_FIELD_VISIBILITY_BY_FIELD_NAME[field_name].includes(report.report_type))) {
    return false;
  }

  return true;
};

export const displayPreFillContent = (report_type) => {
  return !['cross_fund_report', 'success_story'].includes(report_type);
};

const GeneralDetails = ({report, generalFormRef, advancedFormRef, phase, formOptions, dispatch, onReportUpdate, editMode, setEditMode}) => {
  // Cross fund reports are sent to other funds (which is a superclass of project)
  const projectInitialValue = (report.project_ids || [])
    .map(pid => (report.report_type == 'cross_fund_report' ? formOptions.available_funds : formOptions.available_projects)
      .find(ap => ap.value.toString() == pid)
    ).filter(id => id);

  const projectFieldTitle = report.report_type == 'cross_fund_report' ? I18n.t('cross_tenant_report_targets') : I18n.t('projects')
  const projectFieldOptions = report.report_type == 'cross_fund_report' ? formOptions.available_funds : formOptions.available_projects;

  const [projects, setProjects] = useState(projectInitialValue);

  const handleProjectSelect = (projectIds) => { setProjects(projectFieldOptions.filter(o => projectIds.includes(o.value))) }
  const handleOnChangeSelectorInput = (data) => { handleProjectSelect(data.map(o => o.value)) };

  const onSave = (e) => {
    e.preventDefault();
    const serializedData = jQuery(generalFormRef.current).serialize() + '&' + jQuery(advancedFormRef.current).serialize();

    jQuery.post(monitoring_report_update_path({id: report.id, _options: true}), serializedData, (res) => {
      setEditMode(false);
      onReportUpdate(res.report); 
      dispatch({type: ACTION_TYPES.UPDATE_DATA, payload: {data: res.data}})
    });
  }

  return (
    <form ref={generalFormRef}>
      <Row>
        <Col>
          <h3 style={{ display: "inline-block" }}>{I18n.t("general")}</h3>
          {!editMode && (
            <Button
              className="pull-right"
              onClick={(e) => {
                e.preventDefault();
                setEditMode(!editMode);
              }}
            >
              <i className="fa fa-pencil" />
            </Button>
          )}
          {editMode && (
            <ActionButtons
              onSave={onSave}
              onCancel={(e) => {
                e.preventDefault();
                Turbo.visit(location.href);
                setEditMode(!editMode);
              }}
            />
          )}
        </Col>
        {displayFormField("monitoring_report[project_ids][]", report) && (
          <>
            <Col size="xs-6" className="mb-1">
              <div>
                <label>{projectFieldTitle}</label>
              </div>
              {editMode ? (
                report.report_type == "cross_fund_report" ? (
                  <Select
                    name="monitoring_report[project_ids][]"
                    options={projectFieldOptions}
                    value={projects.map((p) => p.value)}
                    isMulti={true}
                    onChange={handleProjectSelect}
                  />
                ) : (
                  <ProjectSelectorInput
                    name="monitoring_report[project_ids][]"
                    defaultValues={projects}
                    handleOnChangeSelectorInput={handleOnChangeSelectorInput}
                  />
                )
              ) : projects.length > 0 ? (
                <ReadOnlyMultiSelectValues values={projects} />
              ) : (
                <p>
                  <i>{I18n.t("none")}</i>
                </p>
              )}
            </Col>
          </>
        )}
      </Row>
    </form>
  );
}

export const DeadlineReminderEdit = ({deadlineReminders, setDeadlineReminders, afterOnly}) => {

  const handleUpdate = (idx, field, value) => {
    let newReminders = [...deadlineReminders];
    // The before/after changes the sign of the number, before; positive & vv.
    if (field == 'beforeOrAfter') {
      newReminders[idx] = value == 'after' ? Math.abs(newReminders[idx]) : (-1 * Math.abs(newReminders[idx]))
    } else { // Keep the sign of the current value, update the number
      if (afterOnly){
        newReminders[idx] =  Math.abs(value)
      } else {
        newReminders[idx] = newReminders[idx] > 0 ? Math.abs(value) : -1 * Math.abs(value)
      }
    }
    setDeadlineReminders(newReminders);
  } 

  const addRow = (e) => {
    e.preventDefault();
    const newValue = afterOnly ? 1 : -1;
    setDeadlineReminders([...deadlineReminders, newValue]);
  };
  
  const removeRow = (idx) => {let newArr = [...deadlineReminders]; newArr.splice(idx,1); setDeadlineReminders(newArr)}

return <>
  {deadlineReminders.map((dr, idx) => <div key={'reminder-row-' + idx}>
    <div className="input-group mb-1" style={{maxWidth: 250}}>
      <input type='hidden'
        name={`monitoring_report[deadline_reminders[${idx}]]`}
        value={dr}
      />
      <NumberInput 
        type="text" 
        className='form-control' 
        value={Math.abs(dr)} 
        onChange={(e) => {handleUpdate(idx, 'days', e.target.value)}}
      /> 
      <div className="input-group-addon">{Math.abs(dr) == 1 ? I18n.t('unit_day') : I18n.t('unit_days')}</div>
      {afterOnly ? (
              <div className="input-group-addon">{I18n.t('after')}</div>
            ) : (
              <select
                className="form-control"
                style={{ minWidth: 100 }}
                value={dr > 0 ? 'after' : 'before'}
                onChange={(e) => {
                  handleUpdate(idx, 'beforeOrAfter', e.target.value);
                }}
              >
                <option value="before">{I18n.t('before')}</option>
                <option value="after">{I18n.t('after')}</option>
              </select>
            )}
      <button className="btn btn-sm btn-danger" style={{ position: 'absolute', right: '-46px', top: '2px'}} onClick={(e) => {e.preventDefault(); removeRow(idx)}}><i className="fa fa-trash"></i></button>
    </div>
  </div>)}
    <button className="btn btn-sm btn-primary" onClick={addRow}>+ {I18n.t('add')}</button>
  </>

}

const NumberInput = (props) => {
  const onChange = (e) => {
    e.target.value = e.target.value.replaceAll(/\D/g, '')
    props.onChange(e)
  }
  return <input {...props} onChange={onChange}/>
}

const AdvancedOptions = ({editMode, formOptions, phase, report, dispatch, formRef, availableLocales = []}) => {
  const [deadline, deadlineInputId] = useDateTimePicker(report.initial_deadline, [editMode], {format: 'YYYY-MM-DD'});
  // const [publish, publishInputId] = useDateTimePicker(null, [editMode]);
  const [deadlineReminders, setDeadlineReminders] = useState((phase.body || {})?.deadline_reminders || [])
  const [disableCreatingNewDrafts, setDisableCreatingNewDrafts] = useState((!(phase) || !(phase.body?.disable_creating_new_drafts)) ? false : phase.body.disable_creating_new_drafts)
  const [allowSelectPrefillSource, setAllowSelectPrefillSource] = useState((!(phase) || !(phase.body?.allow_select_prefill_source)) ? false : phase.body.allow_select_prefill_source)
  const [allowUseOrganization, setAllowUseOrganization] = useState((!(phase) || !(phase.body?.allow_use_organization)) ? false : phase.body.allow_use_organization)
  const [allowCreateOrganization, setAllowCreateOrganization] = useState((!(phase) || !(phase.body?.allow_create_organization)) ? false : phase.body.allow_create_organization)

  const [allowOnlyAssignedSubmissions, setAllowOnlyAssignedSubmissions] = useState((!(phase) || !(phase.body?.allow_only_assigned_submissions)) ? false : phase.body.allow_only_assigned_submissions)
  const [allowSingleSubmission, setAllowSingleSubmission] = useState((!(phase) || !(phase.body?.allow_single_submission)) ? false : phase.body.allow_single_submission)

  const [useTenantBackground, setUseTenantBackground] = useState(phase?.body?.use_tenant_background === undefined ? true : phase.body.use_tenant_background)
  const [useUseCustomBackgroundColor, setUseCustomBackgroundColor] = useState(phase?.body?.use_custom_background_color || '')
  const [useCustomThankYouPageTexts, setUseCustomThankYouPageTexts] = useState(!phase?.body?.use_custom_thank_you_page_texts ? false : phase.body.use_custom_thank_you_page_texts)
  const [customThankYouPageTexts, setCustomThankYouPageTexts] = useState(phase?.body?.custom_thank_you_page_texts || {title: {}, message: {}})
  const [useCustomThankYouPageImage, setUseCustomThankYouPageImage] = useState(!phase?.body?.use_custom_thank_you_page_image ? false : phase.body.use_custom_thank_you_page_image)
  const [customThankYouPageImage, setCustomThankYouPageImage] = useState(phase?.body?.custom_thank_you_page_image || {})

  const [assignees, setAssignees] = useState(
    ((phase.body || {}).assignees || []).map((r) => ({ type: r.type, id: parseInt(r.id, 10) }))
  );

  const handleAssigneeSelect = (selected) => {
    setAssignees(selected.map((s) => ({ type: s.split(":")[0], id: s.split(":")[1] })));
  };

  const assigneeDetails = assignees
    .map((r) =>
      formOptions.assignable_submitters?.find((ap) => ap.id == r.id && ap.type == r.type)
    )
    .filter(Boolean);

  const assignableSubmittersOptions = useMemo(() => {
    const groups = [
      { label: I18n.t('individual_users'), type: 'user' },
      { label: I18n.t('user_roles'), type: 'user_type' },
    ];
    const data = formOptions.assignable_submitters || [];

    return groups
      .map(({ label, type }) => {
        const options = data.filter(a => a.type === type).map(a => ({value: `${a.type}:${a.id}`, label: a.label}));
        return options.length ? { label, options } : null;
      })
      .filter(Boolean);
  }, [])

  return  (
    <>
      <form ref={formRef}>
        {displayFormField("monitoring_report[initial_deadline]", report) && (
          <Row>
            <Col size="xs-6">
              <div className="form-group">
                <div>
                  <label>{I18n.t("deadline")}</label>
                </div>
                {editMode ? (
                  <input
                    defaultValue={deadline ? dayjs(deadline).format("YYYY-MM-DD") : ""}
                    name="monitoring_report[initial_deadline]"
                    type="text"
                    className="form-control"
                    id={deadlineInputId}
                  />
                ) : (
                  <p>
                    {deadline ? DT.dt(deadline) : <i>{I18n.t("none")}</i>}
                  </p>
                )}
              </div>
            </Col>
          </Row>
        )}
        {displayFormField("monitoring_report[deadline_reminders]", report) && (
          <Row>
            <Col size="xs-6">
              <div className="form-group">
                <div>
                  <PhaseSectionElement title_key='deadline_reminders'/>
                </div>
                {editMode ? (
                  <DeadlineReminderEdit
                    deadlineReminders={deadlineReminders}
                    setDeadlineReminders={setDeadlineReminders}
                    afterOnly={false}
                  />
                ) : deadlineReminders.length > 0 ? (
                  deadlineReminders.map((dr, idx) => (
                    <div key={"reminder-row-" + idx}>{`${Math.abs(dr)} ${
                      Math.abs(dr) == 1 ? I18n.t("unit_day") : I18n.t("unit_days")
                    } ${I18n.t(dr > 0 ? "after" : "before")}`}</div>
                  ))
                ) : (
                  <p>
                    <i>{I18n.t("none")}</i>
                  </p>
                )}
              </div>
            </Col>
          </Row>
        )}

        {displayFormField("monitoring_report[assignees]", report) && (
          <Row>
            <Col size="xs-6">
              <div className="form-group">
                <div>
                  <label>{I18n.t("assignees")}</label>
                </div>
                {editMode ? (
                  <>
                    <Select
                      options={assignableSubmittersOptions}
                      value={assignees.map((a) => `${a.type}:${a.id}`)}
                      onChange={handleAssigneeSelect}
                      isMulti
                    />
                    {assignees.map((a) => (
                      <div key={`${a.type}-${a.id}`} className="visibility-hidden">
                        <input
                          type="hidden"
                          name="monitoring_report[assignees][][id]"
                          value={a.id}
                        />
                        <input
                          type="hidden"
                          name="monitoring_report[assignees][][type]"
                          value={a.type}
                        />
                      </div>
                    ))}
                    {report.report_type === 'survey' &&
                      <span className="description">{I18n.t("survey_assignee_description")}</span>
                    }
                  </>
                ) : assignees.length > 0 ? (
                  <ReadOnlyMultiSelectValues values={assigneeDetails}/>
                ) : (
                  <p>-</p>
                )}
              </div>
            </Col>
          </Row>
        )}

        {displayFormField("monitoring_report[allow_only_assigned_submissions]", report) && (
          <Row>
            <Col size='xs-6'>
              <div className='form-group'>
                <div>
                  <label>{I18n.t('allow_only_assigned_submissions')}</label>
                </div>
                {editMode ? (
                  <>
                    <ToggleSwitch
                      name={'monitoring_report[allow_only_assigned_submissions]'}
                      isOn={allowOnlyAssignedSubmissions}
                      handleToggle={() => setAllowOnlyAssignedSubmissions(!allowOnlyAssignedSubmissions)}
                    />
                  </>
                ) : <p><i>{I18n.t(allowOnlyAssignedSubmissions ? 'enabled' : 'disabled')}</i></p>
                }
              </div>
            </Col>
          </Row>
        )}

        {displayFormField("monitoring_report[allow_single_submission]", report) && (
          <Row>
            <Col size='xs-6'>
              <div className='form-group'>
                <div>
                  <label>{I18n.t('allow_single_submission')}</label>
                </div>
                {editMode ? (
                  <>
                    <ToggleSwitch
                      name={'monitoring_report[allow_single_submission]'}
                      isOn={allowSingleSubmission}
                      handleToggle={() => setAllowSingleSubmission(!allowSingleSubmission)}
                    />
                  </>
                ) : <p><i>{I18n.t(allowSingleSubmission ? 'enabled' : 'disabled')}</i></p>
                }
              </div>
            </Col>
          </Row>
        )}

        {displayFormField("monitoring_report[disable_creating_new_drafts]", report) && (
          <Row>
            <Col size="xs-6">
              <div className="form-group">
                <div>
                  <label>{I18n.t("disable_new_drafts")}</label>
                </div>
                {editMode ? (
                  <div className='d-flex align-items-center justify-content-start'>
                    <ToggleSwitch
                      name={"monitoring_report[disable_creating_new_drafts]"}
                      isOn={disableCreatingNewDrafts}
                      handleToggle={() => setDisableCreatingNewDrafts(!disableCreatingNewDrafts)}
                    />
                    <span className="description" style={{marginLeft: '1rem'}}>
                      {I18n.t("disable_new_drafts_description")}
                    </span>
                  </div>
                ) : disableCreatingNewDrafts ? (
                  <p>
                    <i>{I18n.t("enabled")}</i>
                  </p>
                ) : (
                  <p>
                    <i>{I18n.t("disabled")}</i>
                  </p>
                )}
              </div>
            </Col>
          </Row>
        )}

        {displayFormField("monitoring_report[allowSelectPrefillSource]", report) && (
          <Row>
            <Col size="xs-6">
              <div className="form-group">
                <div>
                  <PhaseSectionElement title_key='allow_select_prefill_source'/>
                </div>
                {editMode ? (
                  <>
                    <div className='d-flex align-items-center justify-content-start'>
                      <ToggleSwitch
                        name={"monitoring_report[allow_select_prefill_source]"}
                        isOn={allowSelectPrefillSource}
                        handleToggle={() => setAllowSelectPrefillSource(!allowSelectPrefillSource)}
                      />
                      <span className="description" style={{marginLeft: '1rem'}}>
                        {I18n.t("allow_select_prefill_source_description")}
                      </span>
                    </div>
                  </>
                ) : allowSelectPrefillSource ? (
                  <p>
                    <i>{I18n.t("enabled")}</i>
                  </p>
                ) : (
                  <p>
                    <i>{I18n.t("disabled")}</i>
                  </p>
                )}
              </div>
            </Col>
          </Row>
        )}

        {displayFormField('monitoring_report[allow_use_organization]', report) && (
          <Row>
            <Col size='xs-6'>
              <div className='form-group'>
                <div>
                  <label>{I18n.t('allow_use_organization')}</label>
                </div>
                {editMode ?
                  <div className='d-flex align-items-center justify-content-start'>
                    <ToggleSwitch
                      name={'monitoring_report[allow_use_organization]'}
                      isOn={allowUseOrganization}
                      handleToggle={() => setAllowUseOrganization(!allowUseOrganization)}
                    />
                  </div>
                  :
                  <p>
                    <i>{I18n.t(allowUseOrganization ? 'enabled' : 'disabled')}</i>
                  </p>
                }
              </div>
            </Col>
          </Row>
        )}

        {displayFormField('monitoring_report[allow_create_organization]', report) && (
          <Row>
            <Col size='xs-6'>
              <div className='form-group'>
                <div>
                  <label>{I18n.t('allow_create_organization')}</label>
                </div>
                {editMode ?
                  <div className='d-flex align-items-center justify-content-start'>
                    <ToggleSwitch
                      name={'monitoring_report[allow_create_organization]'}
                      isOn={allowCreateOrganization && allowUseOrganization}
                      disabled={!allowUseOrganization}
                      handleToggle={() => setAllowCreateOrganization(!allowCreateOrganization)}
                    />
                  </div>
                  :
                  <p>
                    <i>{I18n.t(allowCreateOrganization ? 'enabled' : 'disabled')}</i>
                  </p>
                }
              </div>
            </Col>
          </Row>
        )}

        {displayFormField("monitoring_report[use_tenant_background]", report) && <>
          <h4>{I18n.t('fill_page_settings')}</h4>
          <Row>
            <Col size='xs-6'>
              <div className='form-group'>
                <div>
                  <label>{I18n.t('use_tenant_background')}</label>
                </div>
                {editMode ?
                  <ToggleSwitch
                    name={'monitoring_report[use_tenant_background]'}
                    isOn={useTenantBackground}
                    handleToggle={() => setUseTenantBackground(!useTenantBackground)}
                  />
                  : <p><i>{I18n.t(useTenantBackground ? 'enabled' : 'disabled')}</i></p>
                }
              </div>
            </Col>
          </Row>

          {!useTenantBackground &&
            <Row>
              <Col size='xs-6'>
                <div className='form-group'>
                  <div>
                    <label>{I18n.t('use_custom_background_color')}</label>
                  </div>
                  {editMode ?
                    <>
                      <CommonColorPicker onChange={(value) => setUseCustomBackgroundColor(value)} value={useUseCustomBackgroundColor}/>
                      <input type='hidden' name={'monitoring_report[use_custom_background_color]'} value={useUseCustomBackgroundColor}/>
                    </>
                    : <div className='form-control' style={{background: useUseCustomBackgroundColor, width: 40}}/>
                  }
                </div>
              </Col>
            </Row>
          }
        </>}

        {displayFormField("monitoring_report[use_custom_thank_you_page_texts]", report) && <>
          <h4>{I18n.t('thank_you_page_settings')}</h4>
          <Row>
            <Col size='xs-6'>
              <div className='form-group'>
                <div>
                <label>{I18n.t('allow_configuring_texts')}</label>
                </div>
                {editMode ?
                  <ToggleSwitch
                    name={'monitoring_report[use_custom_thank_you_page_texts]'}
                    isOn={useCustomThankYouPageTexts}
                    handleToggle={() => setUseCustomThankYouPageTexts(!useCustomThankYouPageTexts)}
                  />
                  : <p><i>{I18n.t(useCustomThankYouPageTexts ? 'enabled' : 'disabled')}</i></p>
                }
              </div>
            </Col>
          </Row>

          {useCustomThankYouPageTexts &&
            availableLocales.map((locale) =>
              <React.Fragment key={locale}>
                <Row>
                  <Col size='xs-6'>
                    <div className='form-group'>
                      <label>{I18n.t('title')} ({locale})</label>
                      {editMode ?
                        <input
                          type='text'
                          className='form-control'
                          name={`monitoring_report[custom_thank_you_page_texts][title][${locale}]`}
                          value={customThankYouPageTexts.title?.[locale]}
                          onChange={(e) => setCustomThankYouPageTexts(prev => update(prev, { title: { [locale]: { $set: e.target.value } } }))}
                        />
                        : <p>{customThankYouPageTexts.title?.[locale] || '-'}</p>
                      }
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col size='xs-6'>
                    <div className='form-group'>
                      <label>{I18n.t('message')} ({locale})</label>
                      {editMode ?
                        <textarea
                          className='form-control'
                          name={`monitoring_report[custom_thank_you_page_texts][message][${locale}]`}
                          value={customThankYouPageTexts.message?.[locale]}
                          onChange={(e) => setCustomThankYouPageTexts(prev => update(prev, { message: { [locale]: { $set: e.target.value } } }))}
                        />
                        : <p>{customThankYouPageTexts.message?.[locale] || '-'}</p>
                      }
                    </div>
                  </Col>
                </Row>
              </React.Fragment>
            )
          }
        </>}

        {displayFormField("monitoring_report[use_custom_thank_you_page_image]", report) && <>
          <Row>
            <Col size='xs-6'>
              <div className='form-group'>
                <div>
                  <label>{I18n.t('allow_using_custom_representative_img')}</label>
                </div>
                {editMode ?
                  <ToggleSwitch
                    name={'monitoring_report[use_custom_thank_you_page_image]'}
                    isOn={useCustomThankYouPageImage}
                    handleToggle={() => setUseCustomThankYouPageImage(!useCustomThankYouPageImage)}
                  />
                  : <p><i>{I18n.t(useCustomThankYouPageImage ? 'enabled' : 'disabled')}</i></p>
                }
              </div>
            </Col>
          </Row>

          {useCustomThankYouPageImage &&
            <Row>
              <Col size='xs-6'>
                <div className='form-group'>
                  {editMode && <>
                    <ImageUploadButton
                      onChange={(val) => setCustomThankYouPageImage(val)}
                      {...{
                        value: customThankYouPageImage,
                        permissions: {public: true},
                        maxItems: 1,
                        minCroppedWidth: 150,
                        minCroppedHeight: 150,
                        closeOnChange: true,
                        hideSaveButton: true,
                        variantSet: 'original'
                      }}
                    />
                    <input type='hidden' name={'monitoring_report[custom_thank_you_page_image]'} value={JSON.stringify(customThankYouPageImage)}/>
                  </>
                  }
                  {customThankYouPageImage?.path &&
                    <img src={`${customThankYouPageImage.path}`} style={{minHeight: '150px', maxHeight: '300px'}}/>
                  }
                </div>
              </Col>
            </Row>
          }
        </>}
      </form>
    </>
  )
}

const FillPhaseEdit = ({phase, ...rest}) => {
  const [isAdvancedShown, setIsAdvancedShown] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const {formOptions, report, dispatch, onReportUpdate, availableLocales = []} = rest;

  const generalFormRef = useRef(null);
  const advancedFormRef = useRef(null);

  return (
    <>
      <GeneralDetails
        phase={phase}
        report={report}
        dispatch={dispatch}
        formOptions={formOptions}
        onReportUpdate={onReportUpdate}
        isAdvancedShown={isAdvancedShown}
        editMode={editMode}
        setEditMode={setEditMode}
        generalFormRef={generalFormRef}
        advancedFormRef={advancedFormRef}
      />
      <PhaseCollapser isCollapsed={!isAdvancedShown} handleClick={() => setIsAdvancedShown(!isAdvancedShown)} />
      <div className={`${isAdvancedShown ? '' : 'hidden'}`}>
        <AdvancedOptions
          editMode={editMode}
          formOptions={formOptions}
          phase={phase}
          report={report}
          dispatch={dispatch}
          formRef={advancedFormRef}
          availableLocales={availableLocales}
        />
      </div>
      <FormList phase={phase} {...rest} /><hr />
      <div className={`${isAdvancedShown ? '' : 'visibility-hidden'}`}>
        {displayPreFillContent(report.report_type) && <>
          <PreFillContextList {...{phase, report, dispatch, availablePreFillContexts: formOptions.available_prefill_contexts}} /><hr />
        </>}
        <FormDataCaptureList {...{phase, report, dispatch}} />
      </div>
    </>
  );
};

export default FillPhaseEdit;